import React, { useEffect } from 'react';
import { Typography } from 'antd';
import {
  gql,
  useQuery,
  useNavigate,
  useParams,
  useLocation,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  AppContent,
  Banner,
  ErrorHandler,
  Gutter,
  Icon,
  Link,
  LoadingState,
  Tabs,
} from '@common/components';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { ShiftTimesList, UnitShiftGapTagsTable } from '@scheduling/components';
import { OrganizationUnitDetails } from '@institutions/components';
import { IntegrationForm } from '@scheduling/components/admin-organization-unit-integration';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { buildDialogUrl } from '@common/services';

const adminOrgUnitViewQuery = gql`
  query AdminOrganizationUnitPageQuery($orgUnitId: ID!, $scope: String!) {
    organizationUnit(uuid: $orgUnitId, scope: $scope) {
      id
      name
      visible
      canUpdate {
        value
      }
    }
  }
`;

type adminOrgUnitViewQueryResponse = {
  organizationUnit: {
    id: string;
    name: string;
    visible: boolean;
    canUpdate: {
      value: boolean;
    };
  };
};

type adminOrgUnitViewQueryVariables = {
  orgUnitId: string;
  scope: string;
};

const { Title } = Typography;

export function CannotEditUnitBanner() {
  return (
    <Gutter size="lg" direction="top">
      <Banner type="info" fontSize="16px">
        You don&apos;t have permission to make edits.
      </Banner>
    </Gutter>
  );
}

export function AdminOrganizationUnitPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const { organizationUnitId = '', tabKey = 'unit-details' } = useParams<{
    organizationUnitId: string;
    tabKey: 'unit-details' | 'shifts' | 'integration' | 'shift-tags';
  }>();

  const handleTabChange = (key: string) =>
    navigate(location.pathname.replace(`/${tabKey}`, `/${key}`));

  const { data, error, loading } = useQuery<
    adminOrgUnitViewQueryResponse,
    adminOrgUnitViewQueryVariables
  >(adminOrgUnitViewQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      orgUnitId: organizationUnitId,
      scope: 'organization',
    },
  });

  useEffect(
    () =>
      setBreadcrumbs([
        {
          name: 'Admin',
          path: '/works/admin',
        },
        {
          name: 'Locations',
          path: ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE,
        },
        {
          name: data?.organizationUnit?.name,
          path: `${ORGANIZATION_ADMIN_MENU.LOCATIONS.ROUTE}/unit/${organizationUnitId}/${tabKey}`,
          privateInfo: true,
        },
      ]),
    [data?.organizationUnit?.name, organizationUnitId, tabKey]
  );

  if (error) {
    return <ErrorHandler error={error} log="AdminOrganizationUnitPage" />;
  }

  if (loading) {
    return <LoadingState />;
  }

  const showShiftTimesList =
    currentMembership.canShowOrganizationUnitShiftConfigs;
  const showIntegration = currentMembership.canShowOrganizationUnitIntegration;

  const canEdit = data?.organizationUnit?.canUpdate?.value || false;
  const isMembershipLinkedToUnit = data?.organizationUnit?.visible || false;
  const isTypeShifts = tabKey === 'shifts';
  const isTypeShiftTags = tabKey === 'shift-tags';
  const integrationsEnabled = currentMembership.integrationsEnabled;

  const editDrawerUrl = buildDialogUrl('organization-unit-edit-drawer', {
    props: {
      organizationUnitId,
    },
  });

  return (
    <>
      <AppContent className="admin-organization-unit-view">
        <Title level={2} style={{ marginBottom: 24 }}>
          {data?.organizationUnit?.name}
          {canEdit && (
            <span style={{ marginLeft: 16 }}>
              <Link to={editDrawerUrl}>
                <Icon icon="pencil" size="xl" color="elm" />
              </Link>
            </span>
          )}
        </Title>
        <Tabs
          activeKey={tabKey}
          defaultActiveKey={tabKey}
          onTabClick={handleTabChange}
          tabBarGutter={0}
          tabBarExtraContent={
            <>
              {isTypeShifts && (
                <div id="wk-portal-target:location-shift-times-list-extra-actions"></div>
              )}
              {isTypeShiftTags && (
                <div id="wk-portal-target:location-shift-tags-table-extra-actions"></div>
              )}
            </>
          }
        >
          <Tabs.Pane tab="Details" key="unit-details">
            {!canEdit && <CannotEditUnitBanner />}
            <OrganizationUnitDetails organizationUnitId={organizationUnitId} />
          </Tabs.Pane>
          {showShiftTimesList && (
            <Tabs.Pane tab="Shift Times" key="shifts">
              {!canEdit && <CannotEditUnitBanner />}
              <ShiftTimesList
                organizationUnitId={organizationUnitId}
                canEdit={canEdit}
                triggerPortal={isTypeShifts}
                integrationsEnabled={integrationsEnabled}
              />
            </Tabs.Pane>
          )}
          {showIntegration && (
            <Tabs.Pane tab="Integration" key="integration">
              {!canEdit && <CannotEditUnitBanner />}
              <IntegrationForm
                organizationUnitId={organizationUnitId}
                disabled={!canEdit}
              />
            </Tabs.Pane>
          )}
          <Tabs.Pane tab="Shift Tags" key="shift-tags">
            <Gutter size="md" direction="vertical">
              <UnitShiftGapTagsTable
                organizationUnitId={organizationUnitId}
                triggerPortal={isTypeShiftTags}
                isMembershipLinkedToUnit={isMembershipLinkedToUnit}
              />
            </Gutter>
          </Tabs.Pane>
        </Tabs>
      </AppContent>
      <style jsx>{`
        :global(.admin-organization-unit-view .ant-tabs-tab) {
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 1.4px;
          padding: 8px 16px 10px 16px;
          margin: 0;
        }
      `}</style>
    </>
  );
}
